import React from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Copy, ShieldAlert, Flame, Upload } from "lucide-react";
import { vibrate } from "@/lib/api";
import ShareResult from "@/components/ShareResult";

const SEV = {
  high: { c: "text-red-400", b: "border-red-500/40", bg: "bg-red-500/10", label: "Élevé" },
  medium: { c: "text-amber-400", b: "border-amber-500/40", bg: "bg-amber-500/10", label: "Moyen" },
  low: { c: "text-yellow-300", b: "border-yellow-500/30", bg: "bg-yellow-500/10", label: "Léger" },
};

function ScoreRing({ score, label, danger }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = danger ? (pct < 40 ? "#ef4444" : pct < 70 ? "#f59e0b" : "#10b981") : "#ff007f";
  return (
    <div className="relative w-32 h-32">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="42" fill="none" stroke="#27272a" strokeWidth="8" />
        <motion.circle
          cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={264}
          initial={{ strokeDashoffset: 264 }}
          animate={{ strokeDashoffset: 264 - (264 * pct) / 100 }}
          transition={{ duration: 1, ease: "easeOut" }}
          style={{ filter: `drop-shadow(0 0 6px ${color})` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-black" style={{ color }}>{pct}</span>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      </div>
    </div>
  );
}

const Card = ({ children, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className={`glass-card rounded-2xl p-5 ${className}`}
    {...rest}
  >
    {children}
  </motion.div>
);

export function RedFlagsResult({ r }) {
  return (
    <div className="space-y-4">
      <Card className="flex flex-col items-center text-center">
        <ScoreRing score={r.score} label="Santé" danger />
        <p className="font-display text-xl font-bold mt-4">{r.verdict}</p>
      </Card>

      {(r.flags || []).length > 0 ? (() => {
        const isMineWho = (w) => ["moi", "toi", "me", "je"].includes((w || "").toLowerCase());
        const mine = r.flags.filter((f) => isMineWho(f.who));
        const theirs = r.flags.filter((f) => !isMineWho(f.who));
        const otherName = (theirs.find((f) => f.who) || {}).who || "Lui / Elle";
        const FlagCard = ({ f }) => {
          const s = SEV[f.severity] || SEV.low;
          return (
            <Card className={`${s.bg} ${s.b}`}>
              <div className="flex items-center gap-2 mb-1">
                <ShieldAlert size={16} className={s.c} />
                <span className="font-semibold text-sm">{f.title}</span>
                <span className={`ml-auto text-[10px] uppercase font-bold ${s.c}`}>{s.label}</span>
              </div>
              <p className="text-sm text-zinc-300">{f.explanation}</p>
              {f.evidence && <p className="text-xs text-zinc-500 mt-2 italic">“{f.evidence}”</p>}
            </Card>
          );
        };
        return (
          <div className="space-y-5">
            {theirs.length > 0 && (
              <div data-testid="flags-theirs" className="space-y-2">
                <p className="text-xs uppercase tracking-widest text-red-400 font-bold flex items-center gap-1.5">
                  🚩 Ses red flags · {otherName}
                  <span className="text-zinc-600 normal-case tracking-normal">({theirs.length})</span>
                </p>
                {theirs.map((f, i) => <FlagCard key={i} f={f} />)}
              </div>
            )}
            <div data-testid="flags-mine" className="space-y-2">
              <p className="text-xs uppercase tracking-widest text-pink-400 font-bold flex items-center gap-1.5">
                🫵 Tes red flags
                <span className="text-zinc-600 normal-case tracking-normal">({mine.length})</span>
              </p>
              {mine.length > 0 ? (
                mine.map((f, i) => <FlagCard key={i} f={f} />)
              ) : (
                <Card className="flex items-center gap-3">
                  <CheckCircle2 className="text-emerald-400" size={18} />
                  <p className="text-sm text-zinc-300">Rien à te reprocher de ce côté 👏</p>
                </Card>
              )}
            </div>
          </div>
        );
      })() : (
        <Card className="flex items-center gap-3">
          <CheckCircle2 className="text-emerald-400" size={20} />
          <p className="text-sm text-zinc-300">Aucun red flag majeur détecté. Profite 😎</p>
        </Card>
      )}

      {(r.greens || []).length > 0 && (
        <Card>
          <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold mb-2">Green flags</p>
          <ul className="space-y-1">
            {r.greens.map((g, i) => (
              <li key={i} className="text-sm text-zinc-300 flex gap-2">
                <CheckCircle2 size={15} className="text-emerald-400 shrink-0 mt-0.5" /> {g}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {r.advice && (
        <Card className="neon-border">
          <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-1">Le conseil du Doc</p>
          <p className="text-sm text-zinc-200">{r.advice}</p>
        </Card>
      )}
    </div>
  );
}

export function RizzResult({ r }) {
  return (
    <div className="space-y-4">
      <Card className="flex items-center gap-5">
        <ScoreRing score={r.score} label="Rizz" />
        <div>
          <div className="font-display text-5xl font-black bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            {r.grade}
          </div>
          <p className="text-sm text-zinc-300 mt-1">{r.verdict}</p>
        </div>
      </Card>

      {(r.breakdown || []).length > 0 && (
        <Card>
          <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-3">Détail du game</p>
          <div className="space-y-3">
            {r.breakdown.map((b, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-300">{b.label}</span>
                  <span className="text-zinc-400">{b.score}</span>
                </div>
                <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${b.score}%` }}
                    transition={{ duration: 0.8 }}
                  />
                </div>
                {b.comment && <p className="text-xs text-zinc-500 mt-1">{b.comment}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3">
        {r.best_line && (
          <Card className="border-emerald-500/30 bg-emerald-500/5">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold mb-1 flex items-center gap-1">
              <Flame size={13} /> Meilleure ligne
            </p>
            <p className="text-sm text-zinc-200 italic">“{r.best_line}”</p>
          </Card>
        )}
        {r.worst_line && (
          <Card className="border-red-500/30 bg-red-500/5">
            <p className="text-xs uppercase tracking-widest text-red-400 font-bold mb-1 flex items-center gap-1">
              <AlertTriangle size={13} /> Moment cringe
            </p>
            <p className="text-sm text-zinc-200 italic">“{r.worst_line}”</p>
          </Card>
        )}
      </div>

      {(r.tips || []).length > 0 && (
        <Card className="neon-border">
          <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-2">Pour monter en niveau</p>
          <ul className="space-y-1">
            {r.tips.map((t, i) => (
              <li key={i} className="text-sm text-zinc-300">• {t}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

const STYLE_META = {
  safe: { label: "Safe", c: "text-emerald-400", b: "border-emerald-500/30" },
  bold: { label: "Bold", c: "text-pink-400", b: "border-pink-500/30" },
  funny: { label: "Funny", c: "text-cyan-400", b: "border-cyan-500/30" },
};

export function RepliesResult({ r }) {
  const copy = (text) => {
    vibrate([100, 50, 100]);
    navigator.clipboard?.writeText(text);
    toast.success("Réponse copiée 🔥");
  };
  return (
    <div className="space-y-3">
      {(r.suggestions || []).map((s, i) => {
        const m = STYLE_META[s.style] || STYLE_META.safe;
        return (
          <Card key={i} className={m.b}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs uppercase tracking-widest font-bold ${m.c}`}>{m.label}</span>
              <button
                data-testid={`copy-reply-${i}`}
                onClick={() => copy(s.text)}
                className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
              >
                <Copy size={13} /> Copier
              </button>
            </div>
            <p className="text-sm text-zinc-100">{s.text}</p>
            {s.why && <p className="text-xs text-zinc-500 mt-2">💡 {s.why}</p>}
          </Card>
        );
      })}
    </div>
  );
}

const ATTACH_META = {
  sécure: { c: "text-emerald-400", b: "border-emerald-500/30", bg: "bg-emerald-500/10" },
  secure: { c: "text-emerald-400", b: "border-emerald-500/30", bg: "bg-emerald-500/10" },
  anxieux: { c: "text-amber-400", b: "border-amber-500/30", bg: "bg-amber-500/10" },
  évitant: { c: "text-cyan-400", b: "border-cyan-500/30", bg: "bg-cyan-500/10" },
  evitant: { c: "text-cyan-400", b: "border-cyan-500/30", bg: "bg-cyan-500/10" },
  désorganisé: { c: "text-red-400", b: "border-red-500/30", bg: "bg-red-500/10" },
};

function AttachPerson({ title, p }) {
  if (!p) return null;
  const m = ATTACH_META[(p.style || "").toLowerCase()] || { c: "text-pink-400", b: "border-pink-500/30", bg: "bg-pink-500/10" };
  return (
    <Card className={`${m.bg} ${m.b}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-widest text-zinc-500 font-bold">{title}</span>
        {typeof p.confidence === "number" && <span className="text-[10px] text-zinc-500">{p.confidence}% sûr</span>}
      </div>
      <p className={`font-display text-2xl font-black capitalize ${m.c}`}>{p.style}</p>
      {(p.signals || []).length > 0 && (
        <ul className="mt-2 space-y-1">
          {p.signals.map((s, i) => <li key={i} className="text-xs text-zinc-400">• {s}</li>)}
        </ul>
      )}
    </Card>
  );
}

export function AttachmentResult({ r }) {
  return (
    <div className="space-y-4">
      {r.verdict && (
        <Card className="text-center">
          <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-1">Style d'attachement</p>
          <p className="font-display text-xl font-bold">{r.verdict}</p>
        </Card>
      )}
      <div className="grid grid-cols-1 gap-3">
        <AttachPerson title="L'autre" p={r.them} />
        <AttachPerson title="Toi" p={r.me} />
      </div>
      {r.advice && (
        <Card className="neon-border">
          <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-1">Le conseil du Doc</p>
          <p className="text-sm text-zinc-200">{r.advice}</p>
        </Card>
      )}
    </div>
  );
}

export function CompatibilityResult({ r }) {
  return (
    <div className="space-y-4">
      <Card className="flex flex-col items-center text-center">
        <ScoreRing score={r.score} label="Compat" danger />
        <p className="font-display text-xl font-bold mt-4">{r.verdict}</p>
      </Card>

      {(r.dimensions || []).length > 0 && (
        <Card>
          <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-3">Les dimensions</p>
          <div className="space-y-3">
            {r.dimensions.map((b, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-300">{b.label}</span>
                  <span className="text-zinc-400">{b.score}</span>
                </div>
                <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${b.score}%` }}
                    transition={{ duration: 0.8 }}
                  />
                </div>
                {b.comment && <p className="text-xs text-zinc-500 mt-1">{b.comment}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3">
        {(r.green_signals || []).length > 0 && (
          <Card className="border-emerald-500/30 bg-emerald-500/5">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold mb-2">Ce qui marche</p>
            <ul className="space-y-1">
              {r.green_signals.map((g, i) => (
                <li key={i} className="text-sm text-zinc-300 flex gap-2">
                  <CheckCircle2 size={15} className="text-emerald-400 shrink-0 mt-0.5" /> {g}
                </li>
              ))}
            </ul>
          </Card>
        )}
        {(r.risk_signals || []).length > 0 && (
          <Card className="border-red-500/30 bg-red-500/5">
            <p className="text-xs uppercase tracking-widest text-red-400 font-bold mb-2">Points de vigilance</p>
            <ul className="space-y-1">
              {r.risk_signals.map((g, i) => (
                <li key={i} className="text-sm text-zinc-300 flex gap-2">
                  <AlertTriangle size={15} className="text-red-400 shrink-0 mt-0.5" /> {g}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {r.advice && (
        <Card className="neon-border">
          <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-1">Le conseil du Doc</p>
          <p className="text-sm text-zinc-200">{r.advice}</p>
        </Card>
      )}
    </div>
  );
}

export function AnalysisConfidence({ messageCount, onImport }) {
  const n = typeof messageCount === "number" ? messageCount : 0;
  const level = n >= 150
    ? { label: "Analyse complète", pct: 100, color: "#10b981" }
    : n >= 30
      ? { label: "Bonne base", pct: 62, color: "#eab308" }
      : { label: "Aperçu rapide", pct: 26, color: "#f59e0b" };
  return (
    <Card data-testid="analysis-confidence">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-400">
          Basé sur <b className="text-white">{n} message{n > 1 ? "s" : ""}</b>
        </span>
        <span className="text-xs font-bold" style={{ color: level.color }}>{level.label}</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <motion.div className="h-full rounded-full" initial={{ width: 0 }}
          animate={{ width: `${level.pct}%` }} transition={{ duration: 0.7 }}
          style={{ background: level.color }} />
      </div>
      {n < 30 && (
        <div className="mt-3 rounded-xl bg-amber-500/10 border border-amber-500/25 p-3" data-testid="analysis-import-nudge">
          <p className="text-xs text-amber-200 leading-relaxed">
            Analyse partielle : le Doc n'a vu que {n} message{n > 1 ? "s" : ""} récent{n > 1 ? "s" : ""}. Pour un bilan complet et fiable, importe l'historique de cette conversation.
          </p>
          {onImport && (
            <button data-testid="analysis-import-btn" onClick={onImport}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-amber-300 border border-amber-500/40 rounded-full px-3 py-1.5 hover:bg-amber-500/10 transition-colors">
              <Upload size={13} /> Importer l'historique .txt
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

export function ResultView({ vertical, result, messageCount, onImport }) {
  if (!result) return null;
  let body = (
    <Card data-testid="analysis-unavailable">
      <p className="text-sm text-zinc-300 font-semibold mb-1">Cette analyse a son propre écran</p>
      <p className="text-xs text-zinc-500">Ouvre la fiche de la relation pour la voir mise en forme.</p>
    </Card>
  );
  if (vertical === "red_flags") body = <RedFlagsResult r={result} />;
  else if (vertical === "rizz_score") body = <RizzResult r={result} />;
  else if (vertical === "reply_suggestions") body = <RepliesResult r={result} />;
  else if (vertical === "attachment_style") body = <AttachmentResult r={result} />;
  else if (vertical === "compatibility") body = <CompatibilityResult r={result} />;
  return (
    <div className="space-y-4">
      {typeof messageCount === "number" && <AnalysisConfidence messageCount={messageCount} onImport={onImport} />}
      {body}
      {["red_flags", "rizz_score", "reply_suggestions", "attachment_style", "compatibility"].includes(vertical) &&
        <ShareResult vertical={vertical} result={result} />}
    </div>
  );
}
